<template>
  <div style="background:#fff; padding:20px;">
    <!-- 顶部搜索 -->
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <a-form-item label="师傅 ID">
          <a-input-number v-model="query.userId" :min="1" placeholder="师傅 user_id" style="width: 140px" />
        </a-form-item>
        <a-form-item label="证书种类">
          <a-select
            v-model="query.certTypeId"
            placeholder="全部"
            allow-clear
            style="width: 180px"
            show-search
            option-filter-prop="children"
          >
            <a-select-option v-for="t in certTypes" :key="t.id" :value="t.id">{{ t.name }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="审核状态">
          <a-select v-model="query.auditStatus" placeholder="全部" allow-clear style="width: 120px">
            <a-select-option :value="0">待审核</a-select-option>
            <a-select-option :value="1">已通过</a-select-option>
            <a-select-option :value="2">已拒绝</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="时间区间">
          <a-range-picker
            v-model="query.dateRange"
            value-format="YYYY-MM-DD"
            :placeholder="['起始日期', '截止日期']"
            style="width: 280px"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </div>

    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <span slot="image" slot-scope="text">
        <img v-if="text" :src="text" alt="证件照片" style="height:60px;cursor:pointer;border-radius:4px;" @click="previewImage(text)" />
        <span v-else>-</span>
      </span>
      <span slot="auditStatus" slot-scope="text">
        <a-tag v-if="text === 0" color="orange">待审核</a-tag>
        <a-tag v-else-if="text === 1" color="green">已通过</a-tag>
        <a-tag v-else-if="text === 2" color="red">已拒绝</a-tag>
        <span v-else>-</span>
      </span>
      <span slot="rejectReason" slot-scope="text">
        <span v-if="text" style="color:#d4380d">{{ text }}</span>
        <span v-else style="color:#999">-</span>
      </span>
      <span slot="action" slot-scope="text, record">
        <template v-if="record.auditStatus === 0">
          <a-popconfirm title="确定通过该证书?" @confirm="handleApprove(record.id)">
            <a v-permission="'app:cert-audit:list'" style="color:#1890ff">通过</a>
          </a-popconfirm>
          <a-divider type="vertical" />
          <a v-permission="'app:cert-audit:list'" style="color:red" @click="openReject(record)">拒绝</a>
        </template>
        <span v-else style="color:#999">已处理</span>
      </span>
    </a-table>

    <!-- 拒绝原因弹窗 -->
    <a-modal
      v-model="rejectVisible"
      title="证书审核拒绝"
      :confirm-loading="rejectSubmitting"
      @ok="submitReject"
      @cancel="closeReject"
    >
      <a-form-model :model="rejectForm" :label-col="{ span: 5 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="拒绝原因">
          <a-textarea v-model="rejectForm.reason" :rows="4" :max-length="200" placeholder="可填写拒绝原因 (最多 200 字)" />
          <div style="text-align:right; color:#999; font-size:12px;">
            {{ (rejectForm.reason || '').length }} / 200
          </div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 大图预览(沿用 antd Modal, 不引入额外组件) -->
    <a-modal v-model="previewVisible" :footer="null" :width="640" title="证件照片预览">
      <img :src="previewUrl" style="width:100%" />
    </a-modal>
  </div>
</template>

<script>
// 证书审核(对应 master-profile-extension Requirement 12)
// 三态状态机: 0 待审核 / 1 已通过 / 2 已拒绝, 终态单调; reject reason 可空,填写时最多 200 字
import moment from 'moment'
import {
  pageCertAudit,
  approveCertAudit,
  rejectCertAudit,
  pageCertType
} from '@/api'

const MAX_DATE_RANGE_DAYS = 366

export default {
  name: 'CertAuditView',
  data() {
    return {
      data: [],
      loading: false,
      certTypes: [],
      query: {
        userId: undefined,
        certTypeId: undefined,
        auditStatus: 0, // 默认仅看待审核
        dateRange: []
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      rejectVisible: false,
      rejectSubmitting: false,
      rejectForm: { id: null, reason: '' },
      previewVisible: false,
      previewUrl: '',
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '师傅 ID', dataIndex: 'userId', width: 100 },
        { title: '证书种类', dataIndex: 'certTypeName', width: 140 },
        { title: '证件照片', dataIndex: 'imageUrl', width: 100, scopedSlots: { customRender: 'image' } },
        { title: '有效期', dataIndex: 'expireAt', width: 120 },
        { title: '审核状态', dataIndex: 'auditStatus', width: 100, scopedSlots: { customRender: 'auditStatus' } },
        { title: '拒绝原因', dataIndex: 'auditRejectReason', ellipsis: true, scopedSlots: { customRender: 'rejectReason' } },
        { title: '上传时间', dataIndex: 'createdAt', width: 180 },
        { title: '审核时间', dataIndex: 'auditAt', width: 180 },
        { title: '操作', key: 'action', width: 140, scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  created() {
    this.loadCertTypes().finally(() => this.loadData())
  },
  methods: {
    loadCertTypes() {
      return pageCertType({ page: 1, pageSize: 100, isEnabled: 1 }).then((res) => {
        const payload = (res && res.data) || {}
        this.certTypes = payload.records || (Array.isArray(payload) ? payload : [])
      })
    },
    // dateRange 跨度 ≤ 366 天前端兜底, 与后端校验同口径
    validateDateRange() {
      const range = this.query.dateRange || []
      if (!range[0] || !range[1]) return { ok: true, startDate: range[0] || undefined, endDate: range[1] || undefined }
      const start = moment(range[0])
      const end = moment(range[1])
      if (end.isBefore(start)) return { ok: false, message: '截止日期不能早于起始日期' }
      const diff = end.diff(start, 'days', true)
      if (diff > MAX_DATE_RANGE_DAYS) return { ok: false, message: '时间跨度不能超过 366 天' }
      return { ok: true, startDate: range[0], endDate: range[1] }
    },
    loadData() {
      const r = this.validateDateRange()
      if (!r.ok) {
        this.$message.error(r.message)
        return
      }
      this.loading = true
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        userId: this.query.userId || undefined,
        certTypeId: this.query.certTypeId,
        auditStatus: this.query.auditStatus,
        startDate: r.startDate,
        endDate: r.endDate
      }
      pageCertAudit(params)
        .then((res) => {
          const payload = (res && res.data) || {}
          const records = payload.records || (Array.isArray(payload) ? payload : [])
          const typeMap = this.certTypes.reduce((map, item) => {
            map[item.id] = item.name
            return map
          }, {})
          this.data = records.map((item) => Object.assign({}, item, {
            certTypeName: typeMap[item.certTypeId] || item.certTypeName || '-'
          }))
          this.pagination.total = payload.total || (Array.isArray(payload) ? payload.length : 0)
        })
        .finally(() => { this.loading = false })
    },
    handleSearch() { this.pagination.current = 1; this.loadData() },
    handleReset() {
      this.query = { userId: undefined, certTypeId: undefined, auditStatus: 0, dateRange: [] }
      this.pagination.current = 1
      this.loadData()
    },
    handleTableChange(p) {
      this.pagination.current = p.current
      this.pagination.pageSize = p.pageSize
      this.loadData()
    },
    previewImage(url) {
      this.previewUrl = url
      this.previewVisible = true
    },
    handleApprove(id) {
      approveCertAudit(id).then(() => {
        this.$message.success('已通过')
        this.loadData()
      })
    },
    openReject(record) {
      this.rejectForm = { id: record.id, reason: '' }
      this.rejectVisible = true
    },
    closeReject() {
      this.rejectVisible = false
      this.rejectForm = { id: null, reason: '' }
    },
    submitReject() {
      const reason = (this.rejectForm.reason || '').trim()
      if (reason.length > 200) { this.$message.error('拒绝原因不能超过 200 字'); return }
      this.rejectSubmitting = true
      rejectCertAudit(this.rejectForm.id, { reason: reason || null })
        .then(() => {
          this.$message.success('已拒绝')
          this.closeReject()
          this.loadData()
        })
        .finally(() => { this.rejectSubmitting = false })
    }
  }
}
</script>
